import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { PortalAuthService } from '../../core/auth/portal-auth.service';
import { SeoService } from '../../core/services/seo.service';
import { PortalNavbarComponent } from '../../core/components/portal-navbar/portal-navbar.component';
import { IonicModule } from '@ionic/angular';
import { RouterLink } from '@angular/router';
import { PortalFooterComponent } from '../../core/components/portal-footer/portal-footer.component';
import { TranslatePipe } from '@ngx-translate/core';

interface PricingVariant {
    icon: string;
    labelKey: string;
    price: string;
    unitKey: string;
    /**
     * Headcount tier: what the base price covers and what each person beyond it costs.
     * Its own line rather than part of noteKey — with a per-business base price the tier IS
     * the offer, and a reader who skips the grey footnote would otherwise take CHF 29 for a
     * flat rate regardless of team size.
     */
    includedKey?: string;
    noteKey: string;
}

interface PricingCard {
    /** Fallback for a product that has no brand mark yet — every current one does. */
    icon: string;
    /** Real brand mark (artworks/<product>); takes precedence over `icon`. */
    iconSrc?: string;
    tagKey: string;
    name: string;
    highlightKeys: string[];
    /** Omitted when externalUrl is set — the button links out instead. */
    route?: string;
    ctaKey: string;
    included?: boolean;
    /** Priced externally (ScreenDeck's own pricing calculator) — no price shown here. */
    externalUrl?: string;
    /** Multiple pricing variants (Chroniq's 3 reporting modes) below the anchor price. */
    variants?: PricingVariant[];
    /**
     * Every card renders a price on the same line, so the cards stay scannable against
     * each other — a flat amount, an "ab" entry price for the variant card, "Inklusive"
     * or "Individuell". Without this the two cards that price differently had no figure
     * at all and the eye lost the row.
     */
    price?: string;
    /** Renders the price as an entry price ("ab CHF 5.00") — the card prices per variant. */
    priceFrom?: boolean;
    unitKey?: string;
    noteKey?: string;
    /** Spans two grid columns on desktop, which is what lets the variants sit side by side. */
    wide?: boolean;
}

@Component({
    selector: 'app-pricing',
    templateUrl: './pricing.page.html',
    styleUrls: ['./pricing.page.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PortalNavbarComponent, IonicModule, RouterLink, PortalFooterComponent, TranslatePipe]
})
export class PricingPage implements OnInit, OnDestroy {
    /** Drives the header variant: app-portal-navbar while signed out; signed in, the
     *  app shell's own header takes over instead (see AppComponent.hideAppShell$). */
    isAuthenticated = false;

    private readonly destroy$ = new Subject<void>();

    constructor(
        private seo: SeoService,
        private readonly portalAuth: PortalAuthService,
        private readonly cdr: ChangeDetectorRef,
    ) { }

    ngOnInit(): void {
        this.seo.apply({ titleKey: 'pricing.seoTitle', descriptionKey: 'pricing.heroSub', path: '/pricing' });

        this.portalAuth.isAuthenticated$.pipe(takeUntil(this.destroy$)).subscribe(auth => {
            this.isAuthenticated = auth;
            this.cdr.markForCheck();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    // Richtwerte (nicht final geprüfte Marktpreise). Vor Live-Schaltung mit dem
    // tatsächlichen Preisrechner/Vertrieb abgleichen. Zwei Ausnahmen, die NICHT
    // "aufgerundet" oder überschrieben werden dürfen:
    //   • ScreenDeck hat ein echtes, eigenes Pricing auf screendeck.ch — dorthin
    //     verlinkt statt eines hier erfundenen Preises.
    //   • Chroniq ist seit 2026-08-07 die freigegebene Preisstruktur (Grundpreis pro
    //     Betrieb + Mitarbeiterstaffel), keine Schätzung.
    // Siehe docs/KONZEPT-Pricing-Seite.md.
    //
    // Order is layout-relevant: the three single-column cards fill the first row, then
    // Chroniq (two columns) plus Portal fill the second exactly. Reordering or adding a
    // card leaves a hole in the grid, so re-check the rows when you touch this list.
    cards: PricingCard[] = [
        {
            icon: 'tv-outline',
            iconSrc: 'assets/icon/screendeck-mark.png',
            tagKey: 'pricing.screendeck.tag',
            name: 'ScreenDeck',
            noteKey: 'pricing.screendeck.note',
            highlightKeys: [
                'pricing.screendeck.pill1', 'pricing.screendeck.pill2',
                'pricing.screendeck.pill3', 'pricing.screendeck.pill4',
            ],
            ctaKey: 'pricing.screendeck.externalCta',
            externalUrl: 'https://www.screendeck.ch',
        },
        {
            icon: 'calendar-outline',
            iconSrc: 'assets/icon/tablestack-mark.png',
            tagKey: 'pricing.tablestack.tag',
            name: 'TableStack',
            price: '39.00',
            unitKey: 'pricing.tablestack.unit',
            noteKey: 'pricing.tablestack.note',
            highlightKeys: [
                'pricing.tablestack.pill1', 'pricing.tablestack.pill2',
                'pricing.tablestack.pill3', 'pricing.tablestack.pill4',
            ],
            route: '/contact',
            ctaKey: 'pricing.discoverFeatures',
        },
        {
            icon: 'qr-code-outline',
            iconSrc: 'assets/icon/menulink-mark.png',
            tagKey: 'pricing.menulink.tag',
            name: 'MenuLink',
            price: '19.00',
            unitKey: 'pricing.menulink.unit',
            noteKey: 'pricing.menulink.note',
            highlightKeys: [
                'pricing.menulink.pill1', 'pricing.menulink.pill2',
                'pricing.menulink.pill3', 'pricing.menulink.pill4',
            ],
            route: '/contact',
            ctaKey: 'pricing.contactCta',
        },
        {
            icon: 'time-outline',
            iconSrc: 'assets/icon/chroniq-mark.png',
            tagKey: 'pricing.chroniq.tag',
            name: 'Chroniq',
            // Entry price of the cheapest variant, so this card carries a figure on the
            // same line as the others; the three variants below give the real breakdown.
            // "ab", because every variant is a base price covering a headcount tier, not a
            // flat rate — the per-person surcharge above the tier lives with each variant.
            price: '10.00',
            priceFrom: true,
            unitKey: 'pricing.chroniq.perMonth',
            highlightKeys: [
                'pricing.chroniq.pill1', 'pricing.chroniq.pill2',
                'pricing.chroniq.pill3', 'pricing.chroniq.pill4',
            ],
            route: '/contact',
            ctaKey: 'pricing.discoverFeatures',
            wide: true,
            variants: [
                {
                    icon: 'phone-portrait-outline',
                    labelKey: 'pricing.chroniq.variant.app',
                    price: '10.00',
                    unitKey: 'pricing.chroniq.perMonth',
                    includedKey: 'pricing.chroniq.variant.appIncluded',
                    noteKey: 'pricing.chroniq.variant.appNote',
                },
                {
                    icon: 'hardware-chip-outline',
                    labelKey: 'pricing.chroniq.variant.terminal',
                    price: '49.00',
                    unitKey: 'pricing.chroniq.perMonth',
                    includedKey: 'pricing.chroniq.variant.terminalIncluded',
                    noteKey: 'pricing.chroniq.variant.terminalNote',
                },
                {
                    icon: 'wallet-outline',
                    labelKey: 'pricing.chroniq.variant.wallet',
                    price: '69.00',
                    unitKey: 'pricing.chroniq.perMonth',
                    includedKey: 'pricing.chroniq.variant.walletIncluded',
                    noteKey: 'pricing.chroniq.variant.walletNote',
                },
            ],
        },
        {
            icon: 'grid-outline',
            iconSrc: 'assets/icon/portal-mark.png',
            tagKey: 'pricing.portal.tag',
            name: 'Gastro Management Portal',
            price: '0.00',
            unitKey: 'pricing.portal.unit',
            noteKey: 'pricing.portal.note',
            highlightKeys: [
                'pricing.portal.pill1', 'pricing.portal.pill2',
                'pricing.portal.pill3', 'pricing.portal.pill4',
            ],
            route: '/home',
            ctaKey: 'pricing.toPortal',
            included: true,
        },
    ];
}
